import React, { useEffect, ReactElement } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import SegmentScript from "segment";
import TrackPageViews from "TrackPageViews";

import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import TagManager from "react-gtm-module";
import { Auth0Provider } from "@auth0/auth0-react";

import CustomSnackbar, { AlertType } from "components/common/Snackbar";
import AppInitializer from "components/common/AppInitializer";
import useScrollToTop from "hooks/useScrollToTop";

import { getUserAdditionalInfo } from "selectors/user";
import { RootState } from "store/types";

import Routes from "routes";
import { captureCampaignParams } from "util/postLoginRedirect";

import { UserAdditionalInfo } from "store/actions/UserAdditionalInfo";
import { GetPlatformConfig } from "store/actions/PlatformConfig";
import { isPlgPath } from "plg/routes";

import "styles/Typography.scss";
import "styles/Colors.scss";
import "styles/BoxShadows.scss";
import "styles/BorderRadius.scss";
import "./App.css";
import "./styles.css";
import "./customFieldsStyling.scss";

interface LoggedInUser {
  userId: string;
  customerId: string;
  type: "edge" | "customer" | "employee";
}

interface CallToAction {
  text?: string;
  onClick?: () => void;
}

interface AppContentProps {
  open: boolean;
  message: string;
  type: AlertType;
  snackbarMessageContent: ReactElement | null;
  prefixIcon: ReactElement | null;
  callToAction: CallToAction;
  environment?: string;
}

function App(): React.ReactElement {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigateTo = useNavigate();

  const {
    open,
    type,
    message = "",
    snackbarMessageContent = null,
    prefixIcon = null,
    callToAction = {},
  } = useSelector((state: RootState) => state?.Utility?.snackbar);

  const platformConfig = useSelector((state: RootState) => state.PlatformConfig.platformConfig);
  const { AUTH0_DOMAIN: domain, AUTH0_CLIENT_ID: clientId, AUTH0_AUDIENCE: audience } = platformConfig?.payload || {};

  // Runs once for the tab's landing URL, regardless of auth state or which route was hit — a
  // deep link may bounce straight to login before any screen-level code runs.
  useEffect(() => {
    captureCampaignParams(window.location.search);
  }, []);

  useEffect(() => {
    const userFromStorage = JSON.parse(localStorage.getItem("loggedInUser") || "null") as LoggedInUser | null;
    const accessToken = localStorage.getItem("accessToken");
    // If user and accesstoken is not null- Send request to fetch additional user details
    if (accessToken && userFromStorage) {
      const reqUrl = {
        edge: `user/${userFromStorage.userId}/summary`,
        customer: `customer/${userFromStorage.customerId}/info`,
        employee: `employee/${userFromStorage.userId}`,
      };
      dispatch(UserAdditionalInfo(reqUrl[userFromStorage.type]));
    } else if (
      (!accessToken || !userFromStorage) &&
      (location.pathname === "/employee" || location.pathname === "/admin")
    ) {
      navigateTo(`${location.pathname}${location.search}${location.hash}`);
    }
  }, []);

  // SegmentScript loads Segment (and PostHog through it) only once ENVIRONMENT is known; screens such as the
  // PLG sign-in never ask for the config, so a visitor landing there would send no analytics at all.
  useEffect(() => {
    if (!platformConfig?.payload) dispatch(GetPlatformConfig({}));
  }, []);

  useEffect(() => {
    if (platformConfig?.payload?.GTM) {
      const { id, auth, preview, isValid } = JSON.parse(platformConfig?.payload?.GTM);
      const tagManagerArgs = {
        gtmId: id,
        auth,
        preview,
      };

      if (isValid) {
        TagManager.initialize(tagManagerArgs);
      }
    }
  }, [platformConfig]);

  const content = (
    <AppInitializer>
      <AppContent
        open={open}
        message={message}
        type={type}
        snackbarMessageContent={snackbarMessageContent}
        prefixIcon={prefixIcon}
        callToAction={callToAction}
        environment={platformConfig?.payload?.ENVIRONMENT}
      />
    </AppInitializer>
  );

  // PRD-3334: PLG pages never get the Auth0 wrapper (see isPlgPath).
  if (!domain || !clientId || !audience || isPlgPath(location.pathname)) {
    return content;
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience,
      }}
    >
      {content}
    </Auth0Provider>
  );
}

// AppContent
function AppContent({ environment, ...props }: AppContentProps): React.ReactElement {
  const userAdditionalInfo = useSelector(getUserAdditionalInfo);
  const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser") || "null") as LoggedInUser | null;

  useScrollToTop({ scrollToTopOnMount: true });

  useEffect(() => {
    if (loggedInUser?.type === "customer" && userAdditionalInfo?.status === "churned") {
      localStorage.clear();
    }
  }, [userAdditionalInfo.status, loggedInUser]);

  return (
    <>
      <CustomSnackbar {...props} />
      <SegmentScript environment={environment} />
      <TrackPageViews />
      <Box
        sx={{
          display: "flex",
          backgroundColor: "#F7F7F7",
          height: "100vh",
        }}
      >
        <CssBaseline />
        <Routes />
      </Box>
    </>
  );
}

export default App;
